import {view, Divider, tokens, Icon} from '@sail/ui';
import {credit} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useState} from 'react';
import {useWidget} from '../../../Widget/useWidget';

type CouponCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.coupon_card.title',
    description: 'Coupon Card title',
    defaultMessage: 'Coupon',
  },
  percentageBased: {
    id: 'conversations_ui.coupon_card.percentage_based',
    description: 'Coupon card percentage based header',
    defaultMessage: 'Percentage-based discount',
  },
  fixedAmount: {
    id: 'conversations_ui.coupon_card.fixed_amount',
    description: 'Coupon card fixed amount discount header',
    defaultMessage: 'Fixed amount discount',
  },
  code: {
    id: 'conversations_ui.coupon_card.code',
    description: 'Coupon card code header',
    defaultMessage: 'Code',
  },
  expires: {
    id: 'conversations_ui.coupon_card.expires',
    description: 'Coupon card expiration header',
    defaultMessage: 'Expires',
  },
  off: {
    id: 'conversations_ui.coupon_card.off',
    description: 'Coupon card off label',
    defaultMessage: 'off',
  },
});

export const CouponCard = ({agentCallResponse}: CouponCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const [isHovered, setIsHovered] = useState(false);

  const {promotion_code: promoCode} = agentCallResponse;
  const {code, coupon} = promoCode || {};
  const {
    id: couponId,
    amount_off: amountOff,
    currency,
    name,
    percent_off: percentOff,
    redeem_by: redeemBy,
  } = coupon || {};

  function displayName() {
    if (name) {
      return name;
    }
    if (amountOff && currency) {
      return intl.formatMessage(messages.fixedAmount);
    }
    if (percentOff) {
      return intl.formatMessage(messages.percentageBased);
    }
  }

  function displayDiscount() {
    if (amountOff && currency) {
      return `${intl.formatNumber(amountOff, {
        style: 'currency',
        currency,
      })} ${intl.formatMessage(messages.off)}`;
    }
    if (percentOff) {
      return `${percentOff}% ${intl.formatMessage(messages.off)}`;
    }
    return null;
  }

  if (!couponId) {
    return null;
  }

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/coupons/${couponId}`,
          },
        });
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'xsmall',
          width: '100%',
          minWidth: 0,
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: tokens.space[75].toString(),
            alignItems: 'center',
          }}
        >
          <Icon
            icon={credit}
            size="xsmall"
            css={{color: isHovered ? 'action.primary' : 'subdued'}}
          />
          <view.span
            css={{
              color: 'subdued',
              font: 'label.small.emphasized',
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          <view.div css={{height: 10, stack: 'x', width: 0}}>
            <Divider />
          </view.div>
          <view.span
            css={{
              font: 'label.small',
              color: 'subdued',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName()}
          </view.span>
        </view.div>
        <view.div
          css={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <view.span
            css={{
              font: 'heading.large',
            }}
          >
            {displayDiscount()}
          </view.span>
        </view.div>
        {code || redeemBy ? (
          <view.div css={{stack: 'x', gap: 'large'}}>
            {code ? (
              <view.div
                css={{
                  stack: 'y',
                  gap: 'xxsmall',
                }}
              >
                <view.span css={{font: 'label.small.emphasized'}}>
                  {intl.formatMessage(messages.code)}
                </view.span>
                <view.span css={{font: 'label.small', color: 'subdued'}}>
                  {code}
                </view.span>
              </view.div>
            ) : null}
            {redeemBy ? (
              <view.div
                css={{
                  stack: 'y',
                  gap: 'xxsmall',
                }}
              >
                <view.span css={{font: 'label.small.emphasized'}}>
                  {intl.formatMessage(messages.expires)}
                </view.span>
                <view.span css={{font: 'label.small', color: 'subdued'}}>
                  {intl.formatDate(redeemBy, {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                  })}
                </view.span>
              </view.div>
            ) : null}
          </view.div>
        ) : null}
      </view.div>
    </view.div>
  );
};
